<template>

    <div class="input-color" :class="classes" v-click-outside="hide">
        <div v-show="show" class="input-color-panel" :style="styles">
            <color-picker v-model="hexValue" :preset-colors="presetColors" :disableAlpha="true" @input="onChange" />
        </div>
        <div ref="toggle" class="input-color-toggle">
            <div class="swatch" :style="`background-color:${hexValue}`" @click.prevent="show = !show"></div>
        </div>
        <v-input class="pr-40" :size="size" placeholder="#FFFFFF" maxlength="7" :model="hex" @input="onInput" />
    </div>

</template>

<style scoped lang="scss">

    .input-color {

        position: relative;

        &.size-sm .input-color-toggle {
            height: 100%;
            padding: 10px;
        }
        &.size-md .input-color-toggle {
            height: 100%;
            padding: 10px;
        }

        .input-color-toggle {
            position: absolute;
            right: 0;
            top: 0;
            z-index: 1;
            .swatch {
                aspect-ratio: 1 / 1;
                border: 1px solid rgba(0,0,0,0.2);
                border-radius: 3px;
                cursor: pointer;
                height: 100%;
                width: auto;
                &:active {
                    opacity: 0.5;
                }
            }
        }

        .input-color-panel {
            position: fixed;
            width: 200px;
            z-index: 100;
            .vc-sketch {
                @include shad(2);
                background: $color-white;
                border: 1px solid $color-rule;
                border-radius: 5px;
                font-family: $font;
                font-weight: 500;
                height: auto;
                overflow: hidden;
                position: absolute;
                width: 100%;
            }
        }
    }

</style>

<style lang="scss">

    .input-color {
        .vc-sketch {
            padding: 10px !important;
        }
        .vc-sketch-field {
            display: none !important;
        }
        .vc-sketch-controls {
            padding: 10px 0 !important;
        }
        .vc-sketch-sliders {
            padding: 0 !important;
        }
        .vc-sketch-color-wrap {
            margin-top: 0 !important;
            overflow: hidden;
        }
        .vc-sketch-presets {
            border-top: 1px solid $color-rule;
            display: flex;
            justify-content: space-between;
            margin: 0 !important;
            padding: 10px 0 0 0 !important;
            .vc-sketch-presets-color {
                margin: 0 !important;
            }
        }
    }

</style>

<script>

import { Sketch } from 'vue-color';
import ClickOutside from 'vue-click-outside';
import rgb2hex from 'rgb2hex';

export default {

    directives: {
        ClickOutside,
    },

    components: {
        'color-picker': Sketch,
    },

    props: {
        model: {
            type: String,
        },
        size: {
            type: String,
            default: 'md',
        },
        position: {
            type: String,
            default: 'top',
        },
        instant: {
            type: Boolean,
            default: true,
        },
        presets: {
            type: Array,
            default: null,
        },
    },

    data() {
        return {
            show: false,
            hex: this.model ? rgb2hex(this.model).hex : '#ffffff',
            hexValue: this.model ? rgb2hex(this.model).hex : '#ffffff',
            tempHex: null,
            defaultPresets: [
                'white',
                'crimson',
                'darkorange',
                'gold',
                'seagreen',
                '#0064ff',
                'indigo',
                'mediumvioletred',
                'black',
            ],
            styles: { left: 0, top: 0 },
            timeout: null,
        }
    },

    computed: {

        presetColors() {
            return this.presets && this.presets.length ? this.presets : this.defaultPresets;
        },

        classes() {
            return {
                'active': this.show,
                'size-sm': this.size === 'sm',
                'size-md': this.size === 'md',
            };
        },

    },

    watch: {

        model(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.hex = newValue ? rgb2hex(newValue).hex : '#ffffff';
                this.hexValue = newValue ? rgb2hex(newValue).hex : '#ffffff';
            }
        },

        show(val) {
            if (val === true) {
                let rectEl = this.$refs.toggle.getBoundingClientRect();
                let x = rectEl.left + rectEl.width - 230;
                let y = 0;
                if (this.position === 'top') y = rectEl.bottom - 239;
                if (this.position === 'bottom') y = rectEl.top + 10;
                this.styles = { left: x + 'px', top: y + 'px' };
            }
            if (!this.instant) {
                if (val === true) {
                    window.addEventListener('mouseup', this.onMouseUp);
                } else {
                    window.removeEventListener('mouseup', this.onMouseUp);
                }
            }
        },

    },

    beforeDestroy() {
        window.removeEventListener('mouseup', this.onMouseUp);
    },

    methods: {

        hide() {
            if (this.show === true) {
                this.show = false;
            }
        },

        onInput(val) {
            if (val && val.length === 7 && this.hex !== val) {
                this.hex = val;
                this.hexValue = val;
                this.$emit('input', this.hex);
                this.tempHex = null;
            }
        },

        onChange(val) {
            this.tempHex = val.a !== 1 ? val.hex8 : val.hex;
            this.hexValue = this.tempHex;
            if (this.instant) {
                this.onInput(this.tempHex);
            }
        },

        onMouseUp(e) {
            if (!this.isSwatchClick(e)) {
                this.onInput(this.tempHex);
            } else {
                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => {
                    this.onInput(this.tempHex);
                }, 10);
            }
        },

        isSwatchClick(e) {
            return e.target.closest('.vc-sketch-presets-color') !== null;
        },

    },

}

</script>
